import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const HypertensionFH = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fef3f2] via-white to-[#fde8e7]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 rounded-full mb-6">
              <i className="ri-heart-pulse-line text-lg text-[#f04e58]"></i>
              <span className="text-sm font-medium text-[#f04e58]">Cardiovascular Risk Factors</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 leading-tight">
              Familial Hypercholesterolemia (FH) and <span className="font-semibold text-[#f04e58]">Hypertension</span>
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed font-light max-w-3xl mx-auto">
              When Two Silent Risks Collide
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-white rounded-2xl border border-gray-100 p-8 mb-8 shadow-sm">
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              High blood pressure and high cholesterol are often discussed separately.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              But when <strong>Familial Hypercholesterolemia (FH)</strong> coexists with <strong>hypertension</strong>, cardiovascular risk does not simply add up — it <strong>multiplies</strong>.
            </p>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              FH causes lifelong elevation of LDL ("bad") cholesterol from birth. Hypertension adds constant mechanical stress on artery walls. Together, they accelerate vascular damage, plaque progression, and the likelihood of heart attack, stroke, and heart failure.
            </p>
            <p className="text-base text-gray-700 leading-relaxed font-semibold text-[#f04e58]">
              Understanding this interaction is essential for prevention and early intervention.
            </p>
          </div>
        </div>
      </section>

      {/* Understanding the Conditions */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-light text-gray-900 mb-8 text-center">
            Understanding the <span className="font-semibold text-[#f04e58]">Conditions</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* What is FH */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border border-red-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58] mb-4">
                <i className="ri-dna-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">What is Familial Hypercholesterolemia?</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                FH is an inherited disorder characterized by:
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-[#f04e58] mt-0.5 flex-shrink-0"></i>
                  <span>LDL cholesterol typically above 190 mg/dL</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-[#f04e58] mt-0.5 flex-shrink-0"></i>
                  <span>Impaired LDL clearance</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-[#f04e58] mt-0.5 flex-shrink-0"></i>
                  <span>Accelerated atherosclerosis</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-[#f04e58] mt-0.5 flex-shrink-0"></i>
                  <span>Strong family history of premature heart disease</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600 mt-4 italic">
                It affects about 1 in 250 people globally — yet most individuals remain undiagnosed.
              </p>
            </div>

            {/* What is Hypertension */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border border-blue-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488] mb-4">
                <i className="ri-heart-pulse-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">What is Hypertension?</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Hypertension (high blood pressure) occurs when force against artery walls remains persistently elevated.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Over time, this leads to:
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-[#0d9488] mt-0.5 flex-shrink-0"></i>
                  <span>Arterial stiffening</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-[#0d9488] mt-0.5 flex-shrink-0"></i>
                  <span>Endothelial damage</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-[#0d9488] mt-0.5 flex-shrink-0"></i>
                  <span>Increased cardiac workload</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-fill text-[#0d9488] mt-0.5 flex-shrink-0"></i>
                  <span>Elevated risk of stroke and heart attack</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600 mt-4 italic">
                Often, hypertension produces no symptoms until complications arise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Dangerous */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl p-8 text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
                <i className="ri-alert-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-4">Why FH + Hypertension Is Particularly Dangerous</h3>
                <p className="text-base leading-relaxed mb-3 text-white/95">
                  FH drives plaque formation through excess LDL.
                </p>
                <p className="text-base leading-relaxed mb-3 text-white/95">
                  Hypertension:
                </p>
                <ul className="space-y-2 text-base text-white/95 mb-4">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill mt-0.5 flex-shrink-0"></i>
                    <span>Increases shear stress on arteries</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill mt-0.5 flex-shrink-0"></i>
                    <span>Damages the endothelial lining</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill mt-0.5 flex-shrink-0"></i>
                    <span>Promotes inflammation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill mt-0.5 flex-shrink-0"></i>
                    <span>Accelerates plaque rupture</span>
                  </li>
                </ul>
                <p className="text-base leading-relaxed font-semibold">
                  When both are present, arteries face both chemical (cholesterol-driven) and mechanical (pressure-driven) injury.
                </p>
                <p className="text-base leading-relaxed mt-2">
                  This dramatically increases cardiovascular risk.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative w-full h-96 rounded-2xl overflow-hidden shadow-lg">
            <img
              src="https://public.readdy.ai/ai/img_res/c17a22f33e2942ce1f3d8481e36299e3.jpg"
              alt="Hypertension and FH Combined Effects"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Key Interactions - Sections 1-8 */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-light text-gray-900 mb-8 text-center">
            How FH and Hypertension <span className="font-semibold text-[#f04e58]">Interact</span>
          </h2>

          <div className="space-y-6">
            {/* 1. Accelerated Atherosclerosis */}
            <div className="bg-white rounded-2xl border-l-4 border-red-500 p-8 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-50 flex-shrink-0">
                  <span className="text-lg font-bold text-red-500">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Accelerated Atherosclerosis</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    Atherosclerosis begins with endothelial injury.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mb-3">
                    <div className="bg-red-50 rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-900 mb-2">In FH:</p>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-fill text-red-500 mt-0.5 flex-shrink-0"></i>
                          <span>LDL particles infiltrate vessel walls</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-fill text-red-500 mt-0.5 flex-shrink-0"></i>
                          <span>Plaque forms early in life</span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-900 mb-2">In hypertension:</p>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-fill text-blue-500 mt-0.5 flex-shrink-0"></i>
                          <span>Elevated pressure weakens arterial lining</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-arrow-right-s-fill text-blue-500 mt-0.5 flex-shrink-0"></i>
                          <span>Microtears allow easier LDL penetration</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    <strong>Together, plaque builds faster and becomes more unstable.</strong>
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Patients with both conditions often show:
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-red-500 mt-0.5 flex-shrink-0"></i>
                      <span>Earlier coronary artery disease</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-red-500 mt-0.5 flex-shrink-0"></i>
                      <span>More diffuse arterial involvement</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-red-500 mt-0.5 flex-shrink-0"></i>
                      <span>Higher coronary calcium scores</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 2. Endothelial Dysfunction Intensified */}
            <div className="bg-white rounded-2xl border-l-4 border-teal-500 p-8 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-50 flex-shrink-0">
                  <span className="text-lg font-bold text-teal-500">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Endothelial Dysfunction Intensified</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    The endothelium regulates blood flow and prevents clot formation.
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    Both FH and hypertension independently impair endothelial function.
                  </p>
                  <p className="text-sm font-semibold text-gray-900 mb-2">Combined effects include:</p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-teal-500 mt-0.5 flex-shrink-0"></i>
                      <span>Reduced nitric oxide production</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-teal-500 mt-0.5 flex-shrink-0"></i>
                      <span>Increased oxidative stress</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-teal-500 mt-0.5 flex-shrink-0"></i>
                      <span>Enhanced inflammatory signaling</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-700 leading-relaxed mt-3">
                    This environment favors clot formation and acute coronary events.
                  </p>
                </div>
              </div>
            </div>

            {/* 3. Arterial Stiffness and Remodeling */}
            <div className="bg-white rounded-2xl border-l-4 border-orange-500 p-8 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-orange-50 flex-shrink-0">
                  <span className="text-lg font-bold text-orange-500">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Arterial Stiffness and Remodeling</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    Hypertension causes arteries to:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700 mb-3">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-orange-500 mt-0.5 flex-shrink-0"></i>
                      <span>Thicken</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-orange-500 mt-0.5 flex-shrink-0"></i>
                      <span>Lose elasticity</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-orange-500 mt-0.5 flex-shrink-0"></i>
                      <span>Become less compliant</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    In FH, lipid deposition contributes to arterial stiffness as well.
                  </p>
                  <p className="text-sm font-semibold text-gray-900 mb-2">The result:</p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                      <span>Increased pulse pressure</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                      <span>Higher workload on the heart</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                      <span>Greater risk of left ventricular hypertrophy</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-600 leading-relaxed mt-3 italic">
                    Arterial stiffness also predicts future cardiovascular events.
                  </p>
                </div>
              </div>
            </div>

            {/* 4. Higher Risk of Coronary Events */}
            <div className="bg-white rounded-2xl border-l-4 border-purple-500 p-8 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-purple-50 flex-shrink-0">
                  <span className="text-lg font-bold text-purple-500">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Higher Risk of Coronary Events</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    Individually, both conditions raise heart attack risk.
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    <strong>Combined, they significantly increase the likelihood of:</strong>
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700 mb-3">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-purple-500 mt-0.5 flex-shrink-0"></i>
                      <span>Premature coronary artery disease</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-purple-500 mt-0.5 flex-shrink-0"></i>
                      <span>Multi-vessel disease</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-purple-500 mt-0.5 flex-shrink-0"></i>
                      <span>Recurrent myocardial infarction</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-purple-500 mt-0.5 flex-shrink-0"></i>
                      <span>Need for bypass surgery</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-600 leading-relaxed italic">
                    Research consistently shows that uncontrolled hypertension worsens outcomes in patients with genetic lipid disorders.
                  </p>
                </div>
              </div>
            </div>

            {/* 5. Stroke Risk Amplified */}
            <div className="bg-white rounded-2xl border-l-4 border-pink-500 p-8 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-pink-50 flex-shrink-0">
                  <span className="text-lg font-bold text-pink-500">5</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Stroke Risk Amplified</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    Hypertension is the strongest modifiable risk factor for stroke.
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    In FH patients:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700 mb-3">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-pink-500 mt-0.5 flex-shrink-0"></i>
                      <span>Carotid plaque may already be present</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-pink-500 mt-0.5 flex-shrink-0"></i>
                      <span>Elevated LDL promotes plaque formation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-pink-500 mt-0.5 flex-shrink-0"></i>
                      <span>High blood pressure increases risk of plaque rupture</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-700 leading-relaxed font-semibold">
                    This combination increases risk of ischemic stroke.
                  </p>
                </div>
              </div>
            </div>

            {/* 6. Left Ventricular Hypertrophy */}
            <div className="bg-white rounded-2xl border-l-4 border-indigo-500 p-8 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-indigo-50 flex-shrink-0">
                  <span className="text-lg font-bold text-indigo-500">6</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Left Ventricular Hypertrophy (LVH)</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    Hypertension forces the heart to pump against higher pressure.
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    Over time:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700 mb-3">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-indigo-500 mt-0.5 flex-shrink-0"></i>
                      <span>The left ventricle thickens</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-indigo-500 mt-0.5 flex-shrink-0"></i>
                      <span>Diastolic function declines</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-indigo-500 mt-0.5 flex-shrink-0"></i>
                      <span>Risk of arrhythmias increases</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    When coronary arteries are also narrowed by FH-related plaque, the hypertrophied heart muscle becomes more vulnerable to ischemia.
                  </p>
                </div>
              </div>
            </div>

            {/* 7. Heart Failure Risk */}
            <div className="bg-white rounded-2xl border-l-4 border-rose-500 p-8 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-rose-50 flex-shrink-0">
                  <span className="text-lg font-bold text-rose-500">7</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Heart Failure Risk</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-3">
                    Chronic hypertension + coronary artery disease from FH can lead to:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700 mb-3">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-rose-500 mt-0.5 flex-shrink-0"></i>
                      <span>Ischemic cardiomyopathy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-rose-500 mt-0.5 flex-shrink-0"></i>
                      <span>Reduced ejection fraction</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-fill text-rose-500 mt-0.5 flex-shrink-0"></i>
                      <span>Symptomatic heart failure</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-700 leading-relaxed font-semibold">
                    This significantly impacts quality of life and long-term survival.
                  </p>
                </div>
              </div>
            </div>

            {/* 8. Residual Risk Despite LDL Control */}
            <div className="bg-white rounded-2xl border-2 border-[#f59e0b]/20 p-8 hover:border-[#f59e0b] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f59e0b]/10 flex-shrink-0">
                  <i className="ri-shield-cross-line text-2xl text-[#f59e0b]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#f59e0b] mb-1">8️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Residual Risk Despite LDL Control</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Even with aggressive LDL lowering:
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f59e0b] mt-0.5 flex-shrink-0"></i>
                    <span>Uncontrolled hypertension continues to damage vessels</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f59e0b] mt-0.5 flex-shrink-0"></i>
                    <span>Plaque may destabilize</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#f59e0b] mt-0.5 flex-shrink-0"></i>
                    <span>Vascular remodeling persists</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Comprehensive risk reduction requires managing both lipids and blood pressure.
              </p>
            </div>

            {/* 9. Treatment Strategy for Dual Risk */}
            <div className="bg-white rounded-2xl border-2 border-[#10b981]/20 p-8 hover:border-[#10b981] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#10b981]/10 flex-shrink-0">
                  <i className="ri-stethoscope-line text-2xl text-[#10b981]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#10b981] mb-1">9️⃣</div>
                  <h3 className="text-xl font-semibold text-gray-900">Treatment Strategy for Dual Risk</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Optimal management includes:
              </p>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Aggressive LDL Reduction</p>
                  <ul className="space-y-1.5 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-medicine-bottle-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>High-intensity statins</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-medicine-bottle-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>Ezetimibe</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-medicine-bottle-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>PCSK9 inhibitors</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-medicine-bottle-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>Inclisiran</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-medicine-bottle-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>LDL apheresis (in severe cases)</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Strict Blood Pressure Control</p>
                  <ul className="space-y-1.5 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-heart-pulse-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>ACE inhibitors or ARBs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-heart-pulse-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>Calcium channel blockers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-heart-pulse-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>Thiazide diuretics</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-heart-pulse-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>Lifestyle modifications (diet, exercise, sodium reduction)</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Lifestyle Measures</p>
                  <ul className="space-y-1.5 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-run-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>Weight management</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-run-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>Physical activity</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-run-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>Smoking cessation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-run-line text-[#10b981] mt-0.5 flex-shrink-0"></i>
                      <span>Mediterranean-style diet</span>
                    </li>
                  </ul>
                </div>
              </div>
              <p className="text-sm font-semibold text-gray-900 mt-4">
                The goal is to reduce cumulative vascular stress.
              </p>
            </div>

            {/* 10. Early Detection Matters */}
            <div className="bg-white rounded-2xl border-2 border-[#06b6d4]/20 p-8 hover:border-[#06b6d4] hover:shadow-lg transition-all">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#06b6d4]/10 flex-shrink-0">
                  <i className="ri-search-eye-line text-2xl text-[#06b6d4]"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#06b6d4] mb-1">🔟</div>
                  <h3 className="text-xl font-semibold text-gray-900">Early Detection Matters</h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Many individuals with FH remain undiagnosed.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                When hypertension is diagnosed first, elevated LDL may be attributed to lifestyle alone — missing the genetic cause.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">If you or a family member has:</p>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#06b6d4] mt-0.5 flex-shrink-0"></i>
                    <span>LDL above 190 mg/dL</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#06b6d4] mt-0.5 flex-shrink-0"></i>
                    <span>Early heart disease</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#06b6d4] mt-0.5 flex-shrink-0"></i>
                    <span>Strong family history of cardiac events</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-[#06b6d4] mt-0.5 flex-shrink-0"></i>
                    <span>Hypertension with unusually high cholesterol</span>
                  </li>
                </ul>
              </div>
              <p className="text-sm font-semibold text-gray-900">
                Consider evaluation for Familial Hypercholesterolemia.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* The Power of Early Intervention */}
      <section className="py-20 bg-gradient-to-br from-[#0d9488] to-[#14b8a6] text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-white/20 mx-auto mb-6">
              <i className="ri-shield-check-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold mb-6">The Power of Early Intervention</h2>
            <p className="text-white/90 leading-relaxed text-lg mb-4">
              When FH is diagnosed early:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-arrow-down-line text-3xl text-white"></i>
                <h3 className="text-xl font-semibold">LDL Reduction</h3>
              </div>
              <p className="text-white/90">LDL can be reduced by 50–70% or more</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-heart-pulse-line text-3xl text-white"></i>
                <h3 className="text-xl font-semibold">Blood Pressure</h3>
              </div>
              <p className="text-white/90">Blood pressure can be optimized</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-speed-down-line text-3xl text-white"></i>
                <h3 className="text-xl font-semibold">Plaque Progression</h3>
              </div>
              <p className="text-white/90">Plaque progression slows</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-shield-line text-3xl text-white"></i>
                <h3 className="text-xl font-semibold">Event Risk</h3>
              </div>
              <p className="text-white/90">Risk of heart attack and stroke decreases significantly</p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
            <p className="text-lg text-white/95 leading-relaxed">
              Reducing LDL early in life lowers <strong className="text-white">"cholesterol-year burden,"</strong> dramatically improving long-term outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Final Takeaway Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Final Takeaway</h2>
            <p className="text-white/90 leading-relaxed mb-4 text-lg">
              Familial Hypercholesterolemia and hypertension are each silent but powerful cardiovascular risk factors.
            </p>
            <p className="text-white font-semibold text-xl mb-8">
              Together, they create a high-risk environment for accelerated vascular disease.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold mb-6 text-center">The Encouraging News?</h3>
            <p className="text-white/90 leading-relaxed text-center text-lg mb-6">
              Both are treatable.
            </p>
            <p className="text-white/90 leading-relaxed text-center text-lg mb-6">
              Early screening, aggressive lipid management, and strict blood pressure control can prevent many of the devastating complications.
            </p>
            <p className="text-white font-semibold text-center text-xl">
              Awareness of FH does not just lower cholesterol — it protects arteries from the combined damage of lifelong LDL exposure and elevated blood pressure.
            </p>
          </div>

          <div className="bg-[#14b8a6]/20 backdrop-blur-sm rounded-2xl p-8 mb-8 text-center">
            <p className="text-lg text-white/95 leading-relaxed">
              If you suspect FH, ask for evaluation. <strong className="text-white">Early action protects both you and your family.</strong>
            </p>
          </div>

          <div className="flex justify-center">
            <Link
              to="/consultation"
              className="inline-flex items-center px-8 py-4 bg-[#f04e58] text-white rounded-full font-semibold hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap shadow-lg"
            >
              Schedule Your Evaluation
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HypertensionFH;
